import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Loader2, Download, Layers, Wand2, FileText, Presentation as PresentationIcon } from "lucide-react";
import { toast } from "sonner";
import { handleQuotaError } from "@/lib/quota";
import { useNavigate } from "react-router-dom";

export default function DocumentsPanel({ accent, gradient, kind }) {
  // kind = "presentation" | "document"
  const nav = useNavigate();
  const [brief, setBrief] = useState("");
  const [audience, setAudience] = useState("investor");
  const [slides, setSlides] = useState(8);
  const [language, setLanguage] = useState("en");
  const [docType, setDocType] = useState("annual_report");
  const [companyName, setCompanyName] = useState("Sanaa AI");
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [last, setLast] = useState(null);

  const list = async () => {
    const path = kind === "presentation" ? "/creative/presentations" : "/creative/documents";
    try { const r = await api.get(path); setItems(r.data); } catch {}
  };
  useEffect(() => { list(); }, [kind]);

  const generate = async () => {
    if (!brief.trim()) return;
    setLoading(true); setLast(null);
    try {
      if (kind === "presentation") {
        const r = await api.post("/creative/presentation/generate", { brief, audience, slides, language });
        setLast(r.data);
        toast.success("Generated"); list();
      } else {
        // SSE streaming — sections arrive one at a time
        await new Promise((resolve, reject) => {
          const token = localStorage.getItem("sanaa_token");
          const url = `${process.env.REACT_APP_BACKEND_URL}/api/creative/document/stream?` +
            `doc_type=${encodeURIComponent(docType)}` +
            `&brief=${encodeURIComponent(brief)}` +
            `&company_name=${encodeURIComponent(companyName)}` +
            `&language=${language}` +
            `&token=${encodeURIComponent(token)}`;
          const es = new EventSource(url);
          let sections = [];
          let cover = { title: brief.slice(0, 60), subtitle: companyName, author: companyName };
          let docId = null;
          es.onmessage = (ev) => {
            try {
              const data = JSON.parse(ev.data);
              if (data.type === "started") {
                docId = data.id;
                setLast({ id: docId, ...cover, sections: [] });
              } else if (data.type === "cover") {
                cover = { title: data.title, subtitle: data.subtitle, author: data.author };
                setLast((prev) => ({ ...(prev || {}), ...cover, sections }));
              } else if (data.type === "section") {
                sections = [...sections, { heading: data.heading, paragraphs: data.paragraphs }];
                setLast((prev) => ({ ...(prev || {}), ...cover, sections, id: docId }));
              } else if (data.type === "done") {
                docId = data.id;
                setLast((prev) => ({ ...(prev || {}), ...cover, sections, id: docId }));
                es.close();
                resolve();
              }
            } catch {/* ignore */}
          };
          es.onerror = () => { es.close(); resolve(); };
          setTimeout(() => { es.close(); resolve(); }, 180000);
        });
        toast.success("Generated"); list();
      }
    } catch (e) {
      if (!handleQuotaError(e, nav)) toast.error(e?.response?.data?.detail || "Generation failed");
    } finally { setLoading(false); }
  };

  const download = async (id) => {
    const url = kind === "presentation"
      ? `${process.env.REACT_APP_BACKEND_URL}/api/creative/presentation/${id}/pptx`
      : `${process.env.REACT_APP_BACKEND_URL}/api/creative/document/${id}/docx`;
    const token = localStorage.getItem("sanaa_token");
    const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (!r.ok) { toast.error("Download failed"); return; }
    const blob = await r.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = kind === "presentation" ? "deck.pptx" : "document.docx";
    a.click();
  };

  const isP = kind === "presentation";

  return (
    <div className="space-y-5" data-testid={`creative-${kind}-panel`}>
      <div>
        <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">{isP ? "Decks" : "Designed documents"}</div>
        <h2 className="font-serif text-3xl mt-1 flex items-center gap-2">
          {isP ? <PresentationIcon className="h-6 w-6" /> : <FileText className="h-6 w-6" />}
          {isP ? "Pitch & investor decks" : "Annual reports & design books"}
        </h2>
        <div className="text-xs text-muted-foreground mt-1">
          {isP ? "Brief in → multi-slide deck out → PPTX export. Brand Brain is honored automatically."
               : "Brief in → designed editorial document → DOCX export."}
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="rounded-sm border border-border/60 bg-card p-4 space-y-3">
          <Field label="Brief">
            <textarea value={brief} onChange={(e) => setBrief(e.target.value)} rows={5}
              data-testid={`${kind}-brief`}
              placeholder={isP ? "e.g. Series A pitch for a GCC quick-commerce startup" : "e.g. FY2026 annual report for a Nigerian fintech bank"}
              className="w-full rounded-sm border border-border bg-background p-3 text-sm resize-none" />
          </Field>
          {isP ? (
            <>
              <Field label="Audience">
                <select value={audience} onChange={(e) => setAudience(e.target.value)} data-testid="pres-audience" className="input-sel">
                  <option value="investor">Investor</option>
                  <option value="board">Board</option>
                  <option value="customer">Customer / sales</option>
                  <option value="internal">Internal / townhall</option>
                  <option value="conference">Conference keynote</option>
                </select>
              </Field>
              <Field label={`Slides: ${slides}`}>
                <input type="range" min={4} max={20} value={slides} onChange={(e) => setSlides(parseInt(e.target.value))} className="w-full" data-testid="pres-slides" />
              </Field>
            </>
          ) : (
            <>
              <Field label="Document type">
                <select value={docType} onChange={(e) => setDocType(e.target.value)} data-testid="doc-type" className="input-sel">
                  <option value="annual_report">Annual report</option>
                  <option value="design_book">Design book / portfolio</option>
                  <option value="brochure">Brochure</option>
                  <option value="whitepaper">Whitepaper</option>
                </select>
              </Field>
              <Field label="Company / studio name">
                <input value={companyName} onChange={(e) => setCompanyName(e.target.value)} data-testid="doc-company" className="input-sel" />
              </Field>
            </>
          )}
          <Field label="Language">
            <select value={language} onChange={(e) => setLanguage(e.target.value)} data-testid={`${kind}-lang`} className="input-sel">
              <option value="en">English</option>
              <option value="ar">العربية</option>
              <option value="fr">Français</option>
              <option value="sw">Kiswahili</option>
            </select>
          </Field>
          <button onClick={generate} disabled={loading || !brief.trim()} data-testid={`${kind}-generate`}
            className="w-full h-10 rounded-sm text-white text-sm font-medium inline-flex items-center justify-center gap-2 disabled:opacity-60"
            style={{ background: gradient }}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
            Generate
          </button>
        </div>

        <div className="rounded-sm border border-border/60 bg-card p-4">
          <div className="text-sm font-medium mb-3 flex items-center gap-2"><Layers className="h-4 w-4" /> Latest output
            {!isP && loading && last && (
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground ml-auto animate-pulse">
                Writing section {((last.sections || []).length) + 1}…
              </span>
            )}
          </div>
          {!last && <div className="text-sm text-muted-foreground">Generated content will preview here.</div>}
          {last && (
            <div className="space-y-3">
              <div>
                <div className="text-xs uppercase tracking-wider text-muted-foreground">{last.subtitle}</div>
                <div className="font-serif text-2xl">{last.title}</div>
              </div>
              <div className="max-h-[420px] overflow-y-auto space-y-3 pr-1">
                {(isP ? last.slides : last.sections || []).map((s, i) => (
                  <div key={i} className="rounded-sm border border-border/40 p-3" data-testid={`${kind}-out-item-${i}`}>
                    <div className="font-medium text-sm">{s.title || s.heading}</div>
                    <ul className="mt-1.5 list-disc list-inside space-y-1 text-xs text-muted-foreground">
                      {(s.bullets || s.paragraphs || []).slice(0, 5).map((b, j) => <li key={j}>{b}</li>)}
                    </ul>
                  </div>
                ))}
              </div>
              <button onClick={() => download(last.id)} data-testid={`${kind}-download`}
                className="inline-flex h-9 items-center gap-2 px-3 rounded-sm border border-border hover:bg-secondary text-sm">
                <Download className="h-4 w-4" /> Download {isP ? "PPTX" : "DOCX"}
              </button>
            </div>
          )}
        </div>
      </div>

      {items.length > 0 && (
        <div>
          <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">History</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {items.slice(0, 12).map((d) => (
              <div key={d.id} className="rounded-sm border border-border/60 bg-card p-3 text-sm" data-testid={`${kind}-history-${d.id}`}>
                <div className="font-medium truncate">{d.brief}</div>
                <div className="text-[11px] text-muted-foreground mt-1">{new Date(d.created_at).toLocaleString()}</div>
                <button onClick={() => download(d.id)} className="mt-2 inline-flex items-center gap-1 text-xs hover:underline">
                  <Download className="h-3 w-3" /> Download
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
      <style>{`.input-sel{width:100%;height:36px;padding:0 10px;background:hsl(var(--background));border:1px solid hsl(var(--border));border-radius:2px;font-size:13px;color:hsl(var(--foreground));outline:none;}`}</style>
    </div>
  );
}
function Field({ label, children }) {
  return <label className="block"><span className="text-xs uppercase tracking-[0.16em] text-muted-foreground">{label}</span><div className="mt-1.5">{children}</div></label>;
}
